import * as React from 'react';
import { useGetIdentity, useNotify, useRefresh } from 'react-admin';

import { getMe } from '../api/authClient';
import { setIdentityCached } from '../api/tokenStorage';

const WARN_STORAGE_PREFIX = 'fbr-license-warn:';

function tryNotifyPreExpiry(
    notify: ReturnType<typeof useNotify>,
    userId: string | undefined,
    message: string | null | undefined
) {
    const msg = message?.trim();
    if (!msg || !userId) return;
    const day = new Date().toISOString().slice(0, 10);
    const key = `${WARN_STORAGE_PREFIX}${userId}:${day}`;
    try {
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, '1');
    } catch {
        /* ignore */
    }
    notify(msg, { type: 'warning', autoHideDuration: 14_000 });
}

/**
 * Polls identity, shows daily pre-expiry warning from server message, reacts to license 403 events.
 */
export function LicenseUiEffects() {
    const { identity } = useGetIdentity();
    const notify = useNotify();
    const refresh = useRefresh();

    const reloadIdentity = React.useCallback(async () => {
        try {
            const me = await getMe();
            setIdentityCached(me);
            tryNotifyPreExpiry(notify, me.id, me.companyLicenseWarningMessage);
            refresh();
        } catch {
            /* ignore */
        }
    }, [notify, refresh]);

    React.useEffect(() => {
        void reloadIdentity();
    }, [reloadIdentity]);

    React.useEffect(() => {
        const onLicense = () => {
            void reloadIdentity();
        };
        window.addEventListener('fbr-license-changed', onLicense);
        return () => window.removeEventListener('fbr-license-changed', onLicense);
    }, [reloadIdentity]);

    React.useEffect(() => {
        const id = window.setInterval(() => {
            void reloadIdentity();
        }, 15 * 60 * 1000);
        return () => window.clearInterval(id);
    }, [reloadIdentity]);

    React.useEffect(() => {
        const onFocus = () => {
            void reloadIdentity();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [reloadIdentity]);

    /** When identity updates from react-admin (e.g. after refresh), still surface the server warning once per day. */
    React.useEffect(() => {
        const uid = (identity as { id?: string } | undefined)?.id;
        const msg = (identity as { companyLicenseWarningMessage?: string } | undefined)?.companyLicenseWarningMessage;
        tryNotifyPreExpiry(notify, uid, msg);
    }, [identity, notify]);

    return null;
}
